import React, { useState } from 'react';
import SEO from "../component/SEO";
import { BsCloudUpload } from "react-icons/bs";
import { toast } from "react-hot-toast";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const AdminGallery = () => {
    const [selectedFiles, setSelectedFiles] = useState([]); // Array of { base64, priority }
    const [loading, setLoading] = useState(false);
    const user = useSelector(state => state.user);
    const navigate = useNavigate();

    const handleUploadImage = async (e) => {
        const files = Array.from(e.target.files);
        
        for (const file of files) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setSelectedFiles((prev) => [...prev, { base64: reader.result, priority: 999 }]);
            };
        }
    };

    const handleRemovePreview = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handlePriorityChange = (index, value) => {
        const priority = parseInt(value) || 999;
        setSelectedFiles((prev) => {
            const updated = [...prev];
            updated[index].priority = priority;
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedFiles.length === 0) return toast.error("Please select images to upload!");

        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                
                // Update loading state to show progress if you had a refined UI for it, 
                // for now we just rely on "Uploading..." text or could use a toast
                const toastId = toast.loading(`Uploading image ${i + 1} of ${selectedFiles.length}...`);

                try {
                    const response = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/gallery/upload`, {
                        method: "POST",
                        headers: {
                            "content-type": "application/json",
                            "Authorization": `Bearer ${localStorage.getItem("token")}`
                        },
                        body: JSON.stringify({ 
                            files: [{ base64: file.base64, priority: file.priority }] 
                        })
                    });

                    const data = await response.json();

                    if (response.ok) {
                        successCount++;
                        toast.success(`Image ${i + 1} uploaded`, { id: toastId });
                    } else {
                        failCount++;
                        toast.error(`Failed: ${data.message || "Unknown error"}`, { id: toastId });
                    }
                } catch (error) {
                    console.error(error);
                    failCount++;
                    toast.error(`Error uploading image ${i + 1}`, { id: toastId });
                }
            }

            if (successCount > 0) {
                toast.success(`Successfully uploaded ${successCount} images!`);
                setSelectedFiles([]);
                if (failCount === 0) {
                    navigate("/gallery");
                }
            }
            
            if (failCount > 0) {
                toast.error(`${failCount} images failed to upload.`);
            }

        } catch (error) {
            console.error(error);
            toast.error("An unexpected error occurred during the upload process");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='p-4 md:p-8 flex flex-col items-center'>
            <SEO title="Admin Gallery - K Bakery" description="Upload and manage gallery images." />
            <form className='bg-white w-full max-w-4xl p-6 rounded-xl shadow-lg' onSubmit={handleSubmit}>
                <h1 className='text-3xl font-bold text-center text-slate-700 mb-6'>Upload Gallery Images</h1>
                
                <label htmlFor='gallery-upload' className='cursor-pointer'>
                    <div className='h-48 w-full bg-slate-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-orange-400 transition-colors'>
                        <span className='text-5xl text-slate-500'><BsCloudUpload /></span>
                        <p className='text-slate-500 mt-2 font-medium'>Click or Drag images to upload</p>
                        <input type='file' id='gallery-upload' accept='image/*' multiple className='hidden' onChange={handleUploadImage} />
                    </div>
                </label>

                {selectedFiles.length > 0 && (
                    <div className='mt-8'>
                        <p className='text-slate-600 font-semibold mb-4'>Selected Images ({selectedFiles.length})</p>
                        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-h-[600px] overflow-y-auto p-4 border rounded-lg bg-slate-50'>
                            {selectedFiles.map((file, index) => (
                                <div key={index} className='bg-white p-3 rounded-lg border shadow-sm flex flex-col gap-3 relative group'>
                                    <div className='aspect-square rounded-md overflow-hidden border'>
                                        <img src={file.base64} alt={`preview-${index}`} className='w-full h-full object-cover' />
                                    </div>
                                    
                                    <div className='flex flex-col gap-1'>
                                        <label className='text-xs font-bold text-slate-500 uppercase'>Priority</label>
                                        <input 
                                            type="number" 
                                            value={file.priority} 
                                            onChange={(e) => handlePriorityChange(index, e.target.value)}
                                            className='w-full p-2 border rounded text-sm focus:ring-2 focus:ring-orange-200 outline-none'
                                            placeholder="Priority (e.g. 1 for high)"
                                        />
                                    </div>

                                    <button 
                                        type='button'
                                        onClick={() => handleRemovePreview(index)}
                                        className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:bg-red-600 transition-colors z-10'
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <button 
                    type='submit' 
                    disabled={loading || selectedFiles.length === 0}
                    className={`w-full mt-8 py-3 rounded-lg text-white font-bold text-lg transition-all ${loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 shadow-md hover:shadow-lg'}`}
                >
                    {loading ? "Uploading..." : "Upload to Gallery"}
                </button>
            </form>
        </div>
    );
};

export default AdminGallery;
